import { useMutation, useQueryClient } from '@tanstack/react-query';
import { patchListEntry } from 'Services';
import { RQ_LIST_BY_ID, RQ_LIST_ENTRIES } from '@brighterion/utils/react-query-constants';
import { callAll } from '@brighterion/utils/general';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { FormikProps } from 'formik';
import { ListEntryApiResponseObj, GeneralListEntry, PanListEntry, RangeListEntry } from '@brighterion/types/listEntry';
import { useTranslation } from 'react-i18next';
import { transformListEntryData } from './useAddNewListEntry';

type PatchListEntryHook = {
    listEntryId: number;
    startTime: string | null; // introduced post 10.7.5
    endTime: string | null; // introduced post 10.7.5
    comment: string;
};

type Props = {
    listId: number;
    onClose: () => void;
    formRef: React.RefObject<FormikProps<ListEntryApiResponseObj | GeneralListEntry | PanListEntry | RangeListEntry>>;
};

const usePatchListEntry = ({ listId, onClose, formRef }: Props) => {
    const queryClient = useQueryClient();
    const { addAlert } = useAlertBanner();
    const { addTechnicalErrorModal } = useReduxModal();
    const { t } = useTranslation();

    const onSuccess = () => {
        // reset state controlling visibility of EditListEntryModal
        onClose();
        addAlert({
            id: 'edit-list-entry-success',
            content: t('ListEntryUpdated'),
            status: 'success'
        });
    };

    const onError = () => {
        addTechnicalErrorModal(t('errorHandling.singleListEntry.genericMessage'), t('List entry could not be saved'));
        // retain user's values
        if (formRef.current) {
            formRef.current.resetForm({
                values: {
                    ...formRef.current.values,
                    isValid: false // for PAN list
                },
                touched: formRef.current.touched,
                errors: formRef.current.errors,
                isSubmitting: false
            });
        }
    };

    return useMutation(
        ({ listEntryId, ...payload }: PatchListEntryHook) =>
            patchListEntry(listId, listEntryId, transformListEntryData(payload)),
        {
            onSuccess: callAll(onSuccess, () => {
                queryClient.invalidateQueries([RQ_LIST_BY_ID, listId]);
                queryClient.invalidateQueries([RQ_LIST_ENTRIES]);
            }),
            onError
        }
    );
};

export default usePatchListEntry;
